// Lightweight client for the local plugins API (dev proxy)
// Fetches a bearer token, then retrieves organization plugins

export interface OrganizationPluginInfo {
  id: string;
  name: string; // label to display
  icon?: string; // icon name for the side nav
  url: string; // URL to load inside iframe
  numericId?: number;
  messagePrefix?: string; // custom message prefix for postMessage communication
}

const BASE_URL = import.meta.env.VITE_PLUGINS_API_BASE_URL as string;

function isValidBaseUrl(url: string | undefined): url is string {
  if (!url) return false;
  if (url.includes('undefined')) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Token response shapes
interface TokenResponseObject {
  token: string;
}

interface TokenNestedResponseObject {
  data: TokenResponseObject;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

function isTokenResponseObject(value: unknown): value is TokenResponseObject {
  return (
    isRecord(value) &&
    typeof (value as Record<string, unknown>).token === 'string'
  );
}

function isTokenNestedResponseObject(
  value: unknown
): value is TokenNestedResponseObject {
  if (!isRecord(value)) return false;
  const inner = (value as Record<string, unknown>).data;
  return isTokenResponseObject(inner);
}

function slugify(input: string): string {
  return (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

async function requestJson<T>(
  input: string | URL | Request,
  init?: globalThis.RequestInit
): Promise<T> {
  const response = await fetch(input, init);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Plugins API error ${response.status}: ${text || response.statusText}`
    );
  }
  // Some endpoints might return raw string; try json then text fallback
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

export async function fetchPluginApiToken(apiKey?: string): Promise<string> {
  if (!isValidBaseUrl(BASE_URL)) {
    throw new Error('Plugins API not configured');
  }
  const effectiveKey =
    apiKey || (import.meta.env.VITE_PLUGIN_API_KEY as string);
  const body = JSON.stringify({ apiKey: effectiveKey });
  const data = await requestJson<unknown>(`${BASE_URL}/auth/token`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
  });
  // Support either a plain string or an object with token property
  if (typeof data === 'string') return data;
  if (isTokenResponseObject(data)) return data.token;
  // Also support { data: { token: string }, ... }
  if (isTokenNestedResponseObject(data)) return data.data.token;
  throw new Error('Unexpected token response from plugins API');
}

export async function fetchOrganizationPlugins(
  organizationId: string,
  token: string
): Promise<OrganizationPluginInfo[]> {
  const url = `${BASE_URL}/plugins/organization/${organizationId}`;
  const data = await requestJson<unknown>(url, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  // Support legacy shape: raw array of OrganizationPluginInfo
  if (Array.isArray(data)) {
    return data as OrganizationPluginInfo[];
  }

  // Support new shape: { success, data: { items: [...] }, message }
  interface ApiItemsResponse {
    data: { items: unknown[] };
  }
  const hasItems = (value: unknown): value is ApiItemsResponse => {
    return (
      isRecord(value) &&
      isRecord((value as Record<string, unknown>).data) &&
      Array.isArray(
        (value as { data?: { items?: unknown[] } }).data?.items ?? null
      )
    );
  };
  if (hasItems(data)) {
    const items = data.data.items;
    const toPlugin = (item: unknown): OrganizationPluginInfo | null => {
      if (!isRecord(item)) return null;
      const r = item as Record<string, unknown>;
      const labelCandidate = (r.label ?? r.name) as unknown;
      const label =
        typeof labelCandidate === 'string' ? labelCandidate : 'Plugin';
      const idCandidate = (r.id ?? r.slug) as unknown;
      const id = typeof idCandidate === 'string' ? idCandidate : slugify(label);
      const urlCandidate = (r.uiUrl ?? r.url) as unknown;
      const url = typeof urlCandidate === 'string' ? urlCandidate : '';
      const iconCandidate = r.icon as unknown;
      const icon =
        typeof iconCandidate === 'string' ? iconCandidate : undefined;
      // Extract numeric ID if available (for API calls)
      const numericIdCandidate = r.id as unknown;
      const numericId =
        typeof numericIdCandidate === 'number'
          ? numericIdCandidate
          : typeof numericIdCandidate === 'string' &&
            !isNaN(parseInt(numericIdCandidate, 10))
          ? parseInt(numericIdCandidate, 10)
          : undefined;
      // Extract messagePrefix if available
      const messagePrefixCandidate = r.messagePrefix as unknown;
      const messagePrefix =
        typeof messagePrefixCandidate === 'string'
          ? messagePrefixCandidate
          : undefined;
      if (!url) return null;
      return { id, name: label, icon, url, numericId, messagePrefix };
    };
    return items.map(toPlugin).filter((p): p is OrganizationPluginInfo => !!p);
  }

  return [];
}

/**
 * Extracts HTTP status code from plugin API error message.
 * Error format: "Plugins API error {status}: {message}"
 */
function extractStatusFromError(error: unknown): number | null {
  if (!(error instanceof Error)) return null;
  const match = error.message.match(/Plugins API error (\d+):/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Fetches plugin configuration from the plugin framework API.
 * Returns the custom field columns configured for the plugin.
 *
 * Error handling:
 * - 404: Returns empty array (no configuration exists - expected case)
 * - 401/403: Throws error (authentication/authorization failure)
 * - Network/other errors: Throws error
 * - Invalid plugin ID: Returns empty array
 */
export async function fetchPluginConfiguration(
  pluginId: string | number,
  token: string,
  organizationId?: string
): Promise<string[]> {
  const numericId =
    typeof pluginId === 'string' ? parseInt(pluginId, 10) : pluginId;
  if (isNaN(numericId)) {
    console.warn(`[fetchPluginConfiguration] Invalid plugin ID: ${pluginId}`);
    return [];
  }

  const url = new URL(
    `${BASE_URL}/plugin-configurations/${numericId}/custom-fields`
  );
  if (organizationId) {
    url.searchParams.set('orgId', organizationId);
  }

  try {
    const data = await requestJson<unknown>(url.toString(), {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (isRecord(data)) {
      const responseData = (data as { data?: unknown }).data;
      if (Array.isArray(responseData)) {
        return responseData.filter(
          (item): item is string => typeof item === 'string'
        );
      }
    }
    if (Array.isArray(data)) {
      return data.filter((item): item is string => typeof item === 'string');
    }
    return [];
  } catch (error) {
    const status = extractStatusFromError(error);

    // 404: No configuration exists - this is expected, return empty array
    if (status === 404) {
      return [];
    }

    // 401/403: Auth errors - rethrow to notify caller
    if (status === 401 || status === 403) {
      throw new Error(
        `[fetchPluginConfiguration] Authentication failed (${status})`
      );
    }

    // Other errors (network, 5xx, etc.) - rethrow
    throw error;
  }
}

/**
 * Validates whether a plugin URL is allowed to be loaded in an iframe.
 *
 * Security checks:
 * - Protocol must be http: or https:
 * - Hostname must match one of the configured allowed hosts (exact match)
 *
 * @param url - The plugin URL to validate
 * @returns true if the URL is safe to load, false otherwise
 */
export function validatePluginUrl(url: string): boolean {
  // Explicit check for empty or invalid input
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(url);

    // Check for allowed hosts configuration
    const allowedHostsEnv = import.meta.env.VITE_ALLOWED_PLUGIN_HOSTS;
    if (!allowedHostsEnv) {
      console.error(
        '[validatePluginUrl] VITE_ALLOWED_PLUGIN_HOSTS not configured'
      );
      return false;
    }

    // Parse and validate allowed hosts
    const allowedHosts =
      typeof allowedHostsEnv === 'string'
        ? allowedHostsEnv
            .split(',')
            .map((h: string) => h.trim())
            .filter((h: string) => h.length > 0)
        : [];

    if (allowedHosts.length === 0) {
      console.error(
        '[validatePluginUrl] No valid hosts configured in VITE_ALLOWED_PLUGIN_HOSTS'
      );
      return false;
    }

    const allowedProtocols = ['http:', 'https:'];
    return (
      allowedHosts.includes(parsed.hostname) &&
      allowedProtocols.includes(parsed.protocol)
    );
  } catch (err) {
    console.warn('[validatePluginUrl] Invalid URL format:', err);
    return false;
  }
}
